import React, { useEffect, useRef, useState } from "react";
import { Table, Dropdown, Icon } from "semantic-ui-react";
import { formatDistance, format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { onSnapshot } from "firebase/firestore";
import {
  dismissWeeklyDeadlineSuggestionForTransactionInDb,
  updateNotificationInDb,
  updateDocInDb,
  updateTransFieldsInDb,
  fetchDocFromDb,
} from "../../app/firestore/firestoreService";
import { docIsTypePdf, docIsTypeImage } from "../../app/common/util/util";
import { openModal } from "../../app/common/modals/modalSlice";
import { toast } from "react-toastify";
import { fetchEmailFromTemplate } from "../emails/emailSlice";
import {
  buildWeeklyDeadlineEmail,
  getWeeklyDeadlineWeekKey,
} from "./weeklyDeadlineEmail";

export default function NotificationsListItem({ notification }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUserProfile } = useSelector((state) => state.profile);
  const [isUpdating, setIsUpdating] = useState(false);
  const [singleDoc, setSingleDoc] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isDocumentComment = notification._isDocumentComment || notification.type === "unresolvedComments";
  const isWeeklyDeadlineEmail = notification.type === "weeklyDeadlineEmail";
  const isTeamNotifyRequest = notification.type === "teamNotifyRequest";
  const isFieldValueSuggestion = notification.type === "listingContractDateSuggestion";
  const teamNotifySingleDocId =
    isTeamNotifyRequest && notification.docIds?.length === 1
      ? notification.docIds[0]
      : null;

  // For team notification requests about exactly one document, fetch that
  // document live so the dropdown can offer "View/Comment" and "Fill Out"
  // based on its current (not the notification-time) status.
  useEffect(() => {
    if (!teamNotifySingleDocId) {
      setSingleDoc(null);
      return;
    }

    const unsubscribe = onSnapshot(
      fetchDocFromDb(teamNotifySingleDocId),
      (snapshot) => {
        setSingleDoc(snapshot.exists() ? { id: snapshot.id, ...snapshot.data() } : null);
      },
      (error) => {
        console.error("Error fetching document for notification:", error);
      }
    );

    return () => unsubscribe();
  }, [teamNotifySingleDocId]);

  async function handleResolve() {
    if (isDocumentComment) {
      // For document comments, navigate to the document with comments open
      handleViewDocuments();
      return;
    }
    setIsUpdating(true);
    try {
      await updateNotificationInDb(notification.id, {
        status: "resolved",
      });
      toast.success("Notification marked as resolved");
    } catch (error) {
      console.error("Error resolving notification:", error);
      toast.error("Failed to resolve notification");
    } finally {
      if (isMountedRef.current) {
        setIsUpdating(false);
      }
    }
  }

  function handleViewTransaction() {
    if (notification.transactionId) {
      navigate(`/transactions/${notification.transactionId}/documents`);
    }
  }

  function handleViewDocuments() {
    if (notification.transactionId && notification.id) {
      // Navigate to the document view with comments panel open
      navigate(`/transactions/${notification.transactionId}/documents/${notification.id}/view`, {
        state: { showComments: true }
      });
    } else if (notification.transactionId) {
      navigate(`/transactions/${notification.transactionId}/documents`);
    }
  }

  function handleViewSingleDoc() {
    if (!singleDoc) return;
    if (docIsTypePdf(singleDoc)) {
      navigate(`/transactions/${notification.transactionId}/documents/${singleDoc.id}/view`);
    } else if (docIsTypeImage(singleDoc)) {
      dispatch(
        openModal({
          modalType: "DocViewImageFileModal",
          modalProps: { doc: singleDoc },
        })
      );
    }
  }

  function handleFillOutSingleDoc() {
    if (!singleDoc) return;
    updateDocInDb(singleDoc.id, { subStatus: "Filling Out Form" });
    navigate(`/transactions/${notification.transactionId}/documents/${singleDoc.id}/fill`);
  }

  function handlePrepareSignatures() {
    if (notification.docId && notification.transactionId) {
      navigate(
        `/transactions/${notification.transactionId}/documents/${notification.docId}/prepare`
      );
    }
  }

  function handleSignDocument() {
    if (notification.docId && notification.transactionId) {
      navigate(
        `/transactions/${notification.transactionId}/documents/${notification.docId}/view`,
        {
          state: {
            openAgentSignModal: true,
            signatureNotificationId: notification.id,
            signatureRoleKey: notification.signatureRoleKey,
          },
        }
      );
    }
  }

  async function dismissWeeklySuggestion({ silent = false } = {}) {
    if (!notification.transactionId) return;
    try {
      await dismissWeeklyDeadlineSuggestionForTransactionInDb(
        notification.transactionId,
        getWeeklyDeadlineWeekKey()
      );
      if (!silent) toast.success("Suggestion dismissed for this week");
    } catch (error) {
      console.error("Error dismissing weekly suggestion:", error);
      if (!silent) toast.error("Failed to dismiss suggestion, sorry.");
    }
  }

  async function handleSendWeeklyEmail() {
    if (!notification.transactionId) return;
    const emailObj = buildWeeklyDeadlineEmail(notification, currentUserProfile);
    dispatch(fetchEmailFromTemplate(emailObj));
    await dismissWeeklySuggestion({ silent: true });
    navigate(`/transactions/${notification.transactionId}/emails`);
  }

  function handleDismissWeeklySuggestion() {
    dismissWeeklySuggestion();
  }

  async function handleApplyFieldValueSuggestion() {
    if (!notification.transactionId || !notification.suggestedField) return;
    setIsUpdating(true);
    try {
      const rawValue = notification.suggestedValue;
      const dateValue = rawValue?.toDate ? rawValue.toDate() : new Date(rawValue);
      await updateTransFieldsInDb(notification.transactionId, {
        [notification.suggestedField]: dateValue,
      });
      await updateNotificationInDb(notification.id, { status: "resolved" });
      toast.success(`${notification.suggestedFieldLabel || "Value"} applied to transaction`);
    } catch (error) {
      console.error("Error applying suggestion:", error);
      toast.error("Failed to apply suggestion");
    } finally {
      if (isMountedRef.current) {
        setIsUpdating(false);
      }
    }
  }

  // Build dropdown options based on notification type
  const dropdownOptions = [];

  if (isWeeklyDeadlineEmail) {
    dropdownOptions.push({
      key: "sendWeeklyEmail",
      text: "Send Email to Clients",
      icon: "mail",
      onClick: handleSendWeeklyEmail,
    });
    dropdownOptions.push({
      key: "view",
      text: "View Transaction",
      icon: "eye",
      onClick: handleViewTransaction,
    });
    dropdownOptions.push({
      key: "dismissWeekly",
      text: "Dismiss for This Week",
      icon: "close",
      onClick: handleDismissWeeklySuggestion,
    });
  } else if (isDocumentComment) {
    // For document comments, show "View Comments" option
    dropdownOptions.push({
      key: "viewComments",
      text: "View Comments",
      icon: "comment",
      onClick: handleViewDocuments,
    });
    dropdownOptions.push({
      key: "view",
      text: "View Transaction",
      icon: "eye",
      onClick: handleViewTransaction,
    });
  } else if (isFieldValueSuggestion) {
    dropdownOptions.push({
      key: "applySuggestion",
      text: "Apply to Transaction",
      icon: "check",
      onClick: handleApplyFieldValueSuggestion,
      disabled: isUpdating,
    });
    dropdownOptions.push({
      key: "view",
      text: "View Transaction",
      icon: "eye",
      onClick: handleViewTransaction,
    });
    dropdownOptions.push({
      key: "dismissSuggestion",
      text: "Dismiss",
      icon: "close",
      onClick: handleResolve,
      disabled: isUpdating,
    });
  } else {
    // Regular notifications
    dropdownOptions.push({
      key: "resolve",
      text: "Mark as Resolved",
      icon: "check",
      onClick: handleResolve,
      disabled: isUpdating,
    });
    dropdownOptions.push({
      key: "view",
      text: "View Transaction",
      icon: "eye",
      onClick: handleViewTransaction,
    });
  }

  // For team notification requests about a single document, add "View/Comment"
  // (Review Requested, Changes Requested, Correction Required) and, if the
  // document can still be filled out, "Fill Out" (Changes Requested, Correction Required)
  if (
    isTeamNotifyRequest &&
    singleDoc &&
    ["Review Requested", "Changes Requested", "Correction Required"].includes(
      notification.requestType
    ) &&
    (docIsTypePdf(singleDoc) || docIsTypeImage(singleDoc))
  ) {
    dropdownOptions.push({
      key: "viewSingleDoc",
      text: "View/Comment",
      icon: "comment",
      onClick: handleViewSingleDoc,
    });

    const canFillOut =
      docIsTypePdf(singleDoc) &&
      singleDoc.status === "In Progress" &&
      singleDoc.isStateForm &&
      !singleDoc.shared &&
      !singleDoc.signingComplete;

    if (
      ["Changes Requested", "Correction Required"].includes(notification.requestType) &&
      canFillOut
    ) {
      dropdownOptions.push({
        key: "fillOutSingleDoc",
        text: "Fill Out",
        icon: "pencil",
        onClick: handleFillOutSingleDoc,
      });
    }
  }

  // Add "Prepare Signatures" option if this is a formReviewed notification
  if (notification.type === "formReviewed" && notification.docId) {
    dropdownOptions.push({
      key: "prepare",
      text: "Prepare Signatures",
      icon: "pencil",
      onClick: handlePrepareSignatures,
    });
  }

  // Add "Sign Document" option for signatureRequired notifications
  if (notification.type === "signatureRequired" && notification.docId) {
    dropdownOptions.push({
      key: "sign",
      text: "Sign Document",
      icon: "pencil alternate",
      onClick: handleSignDocument,
    });
  }

  const getNotificationTypeLabel = (type) => {
    switch (type) {
      case "formReviewed":
        return "Form Reviewed";
      case "unresolvedComments":
        return "Unresolved Comments";
      case "signatureRequired":
        return "Signature Required";
      case "weeklyDeadlineEmail":
        return "Weekly Email Suggestion";
      case "listingContractDateSuggestion":
        return `${notification.suggestedFieldLabel || "Field Value"} Suggestion`;
      case "teamNotifyRequest":
        return notification.requestType || "Team Notification";
      default:
        return type;
    }
  };

  const getIconName = () => {
    if (isWeeklyDeadlineEmail) return "mail";
    if (isFieldValueSuggestion) return "lightbulb";
    if (isDocumentComment) return "comment alternate";
    if (isTeamNotifyRequest) return "bullhorn";
    return "bell";
  };

  const getIconColor = () => {
    if (isWeeklyDeadlineEmail) return "green";
    if (isFieldValueSuggestion) return "yellow";
    if (isDocumentComment) return "orange";
    if (isTeamNotifyRequest) return "teal";
    return "blue";
  };

  // Helper to format date - handle both Firestore timestamps and serialized formats
  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    try {
      let date;
      if (dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else if (dateValue.seconds) {
        date = new Date(dateValue.seconds * 1000);
      } else {
        date = new Date(dateValue);
      }
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch {
      return null;
    }
  };

  // Format a raw suggested field value (Firestore Timestamp, Date, or string) as a date
  const formatSuggestedDate = (dateValue) => {
    if (!dateValue) return "—";
    try {
      const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
      if (isNaN(date.getTime())) return "—";
      return format(date, "MMMM d, yyyy");
    } catch {
      return "—";
    }
  };

  return (
    <Table.Row>
      <Table.Cell>
        <Icon name={getIconName()} color={getIconColor()} />
        {getNotificationTypeLabel(notification.type)}
      </Table.Cell>
      <Table.Cell>
        {isDocumentComment
          ? (notification.transactionTitle || notification.transactionId)
          : (notification.transactionAddress || notification.transactionId)}
      </Table.Cell>
      <Table.Cell>
        {isWeeklyDeadlineEmail ? (
          <span style={{ color: "#888", fontSize: "0.9em" }}>
            {notification.deadlines?.length || 0} deadline
            {(notification.deadlines?.length || 0) === 1 ? "" : "s"} this week
          </span>
        ) : isDocumentComment ? (
          <span style={{ color: "#888", fontSize: "0.9em" }}>
            {notification.unresolvedCommentCount} unresolved
          </span>
        ) : isFieldValueSuggestion ? (
          "Transaction"
        ) : (
          <>
            {notification.partyFirstName && notification.partyLastName
              ? `${notification.partyFirstName} ${notification.partyLastName}`
              : notification.fromFirstName && notification.fromLastName
              ? `${notification.fromFirstName} ${notification.fromLastName}`
              : ""}
            {notification.partyRole && (
              <span style={{ color: "#888", fontSize: "0.9em" }}>
                {" "}
                ({notification.partyRole})
              </span>
            )}
          </>
        )}
      </Table.Cell>
      <Table.Cell>
        {isWeeklyDeadlineEmail ? (
          <div>
            <div style={{ marginBottom: "4px" }}>
              Send clients this week's deadlines:
            </div>
            {notification.deadlines?.map((d) => (
              <div key={d.id} style={{ fontSize: "0.9em", color: "#555" }}>
                • {d.title}
                {d.end ? ` — ${format(d.end, "EEE, MMM do")}` : ""}
              </div>
            ))}
          </div>
        ) : isDocumentComment ? (
          <div style={{ marginBottom: "4px" }}>
            <strong></strong> {notification.name || notification.docName}
          </div>
        ) : isFieldValueSuggestion ? (
          <div>
            <div style={{ marginBottom: "4px" }}>
              {notification.suggestedFieldLabel || "Suggested value"}:{" "}
              <strong>{formatSuggestedDate(notification.suggestedValue)}</strong>
            </div>
            {notification.docName && (
              <div style={{ fontSize: "0.9em", color: "#555" }}>
                From: {notification.docName}
              </div>
            )}
          </div>
        ) : (
          <>
            {notification.docName && (
              <div style={{ marginBottom: "4px" }}>
                <strong></strong> {notification.docName}
              </div>
            )}
            {notification.message && (
              <div style={{ fontStyle: "italic", color: "#555" }}>
                "{notification.message}"
              </div>
            )}
          </>
        )}
      </Table.Cell>
      <Table.Cell>
        {isWeeklyDeadlineEmail
          ? "This week"
          : formatDate(notification.createdAt) || formatDate(notification.updatedAt)}
      </Table.Cell>
      <Table.Cell textAlign="right">
        <Dropdown
          icon="ellipsis vertical"
          floating
          button
          className="icon"
          disabled={isUpdating}
        >
          <Dropdown.Menu direction="left">
            {dropdownOptions.map((option) => (
              <Dropdown.Item
                key={option.key}
                icon={option.icon}
                text={option.text}
                onClick={option.onClick}
                disabled={option.disabled}
              />
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </Table.Cell>
    </Table.Row>
  );
}

